import React from 'react';
import { Circle, Line, Rect, SchematicShell } from './SchematicPrimitives';
import {
  vanityLightBulbCount,
  vanityLightProportions,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Elevation schematic for `overhead-wall-light` — the downlight vanity bar.
 *
 * Matches `overheadWallLightRenderer` in lightingRenderers.js; every number
 * comes from `VANITY_LIGHT` / `vanityLightProportions` in
 * proceduralSpecs.js: a slim bar at the top of the box, N stubs dropping
 * from it, and a frosted bell hanging from each with its mouth open
 * downward at the bottom of the box.
 *
 * Front: the bar band (backplate and rail coincide), then per shade a stub
 * line, the bell trapezoid (neck up, mouth down) and the lamp circle at the
 * mouth. Side: the backplate strip on the wall edge, the rail square held
 * out on its arm, one stub and the bell profile — the other shades sit
 * behind it. Right mirrors left.
 *
 * Variant field: `obj.bulbCount` (explicit shade count). Absent → one shade
 * per 8" of width, the same rule the renderer applies.
 */

/** Kept for existing callers/tests; the rule itself lives in the specs. */
export function resolveVanityLightBulbCount(obj = {}, widthMeters = 0) {
  return vanityLightBulbCount(widthMeters, obj?.bulbCount);
}

export function VanityLightSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  obj = {},
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  const p = vanityLightProportions({ width, height, depth }, obj?.bulbCount);
  const isSide = view === 'left' || view === 'right';
  const stroke = { strokeColor, strokeWidth };
  return (
    <SchematicShell
      width={isSide ? depth : width}
      height={height}
      drawFrame={false}
      {...stroke}
    >
      {isSide ? (
        <g
          transform={
            view === 'right' ? `translate(${depth}, 0) scale(-1, 1)` : undefined
          }
        >
          <BarSide p={p} height={height} {...stroke} />
        </g>
      ) : (
        <BarFront p={p} width={width} height={height} {...stroke} />
      )}
    </SchematicShell>
  );
}
VanityLightSchematic.supportsSideViews = true;

/** Stub, hanging bell and lamp around a vertical axis at `cx`. */
function HangingBell({ p, cx, height, strokeColor, strokeWidth }) {
  const stroke = { strokeColor, strokeWidth };
  const neckY = height - p.neckY;
  const mouthY = height - p.mouthY;
  return (
    <>
      <Line
        x1={cx}
        y1={height - p.railBottomY}
        x2={cx}
        y2={neckY}
        {...stroke}
      />
      <polygon
        points={[
          [cx - p.neckR, neckY],
          [cx + p.neckR, neckY],
          [cx + p.mouthR, mouthY],
          [cx - p.mouthR, mouthY],
        ]
          .map(([x, y]) => `${x},${y}`)
          .join(' ')}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* Lamp at the mouth, seen through the glass. */}
      <Circle cx={cx} cy={mouthY - p.bulbR} r={p.bulbR} {...stroke} />
    </>
  );
}

function BarFront({ p, width, height, strokeColor, strokeWidth }) {
  const stroke = { strokeColor, strokeWidth };
  return (
    <>
      {/* Bar band: backplate and rail coincide in the front view. */}
      <Rect x={0} y={0} width={width} height={p.rail} {...stroke} />
      {p.shadeXs.map((x, i) => (
        <g key={i} data-vanity-shade={i}>
          <HangingBell p={p} cx={width / 2 + x} height={height} {...stroke} />
        </g>
      ))}
    </>
  );
}

function BarSide({ p, height, strokeColor, strokeWidth }) {
  const stroke = { strokeColor, strokeWidth };
  const armY = height - p.armY;
  const railX = p.shadeAxisZ - p.rail / 2;
  return (
    <>
      {/* Backplate strip on the wall edge (x = 0). */}
      <Rect x={0} y={0} width={p.backplateD} height={p.rail} {...stroke} />
      {/* Connector arm out to the rail. */}
      <Line x1={p.backplateD} y1={armY} x2={railX} y2={armY} {...stroke} />
      {/* Rail in section, over the bell axis. */}
      <Rect x={railX} y={0} width={p.rail} height={p.rail} {...stroke} />
      <HangingBell p={p} cx={p.shadeAxisZ} height={height} {...stroke} />
    </>
  );
}
